/**
 * PasskeyButtons — sign-in and register CTAs for WebAuthn/passkeys.
 *
 * Uses the native `navigator.credentials.create()` / `.get()` API —
 * no third-party SDK. Face ID / Touch ID / Windows Hello prompt the
 * user via the browser's platform authenticator, no biometric data
 * ever leaves the device.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Fingerprint, ScanFace, KeyRound } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
/* ── base64url helpers (WebAuthn ceremony carries binary blobs) ─── */
function b64urlToBuf(s) {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const buf = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) buf[i] = raw.charCodeAt(i);
  return buf.buffer;
}
function bufToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s).replace(/=/g, "").replace(/\+/g, "-").replace(/\//g, "_");
}
function pkOptsFromServer(opts) {
  return {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    user: opts.user ? { ...opts.user, id: b64urlToBuf(opts.user.id) } : undefined,
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
      ...c, id: b64urlToBuf(c.id),
    })),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({
      ...c, id: b64urlToBuf(c.id),
    })),
  };
}
function serializeAttestation(cred) {
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bufToB64url(cred.response.attestationObject),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}
function serializeAssertion(cred) {
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      authenticatorData: bufToB64url(cred.response.authenticatorData),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
      signature: bufToB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? bufToB64url(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults(),
  };
}

export function PasskeySignInButton({ email, onSuccess }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== "undefined"
    && window.PublicKeyCredential
    && typeof window.PublicKeyCredential.isConditionalMediationAvailable === "function";
  if (!supported) return null;

  async function signIn() {
    setBusy(true);
    try {
      const { data: opts } = await axios.post(
        `${API}/api/auth/passkey/auth-challenge`, { email: email || undefined });
      const cred = await navigator.credentials.get({
        publicKey: pkOptsFromServer(opts),
      });
      if (!cred) throw new Error("no credential returned");
      const { data: user } = await axios.post(
        `${API}/api/auth/passkey/verify-authentication`,
        { credential: serializeAssertion(cred), email: email || undefined },
        { withCredentials: true },
      );
      toast.success(`${t("login_welcome_back_prefix")}${user.name || user.email}`);
      if (onSuccess) onSuccess(user);
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        toast.error(t("login_passkey_cancelled"));
      } else {
        toast.error(err?.response?.data?.detail
                      || err?.message
                      || t("login_passkey_failed"));
      }
    }
    setBusy(false);
  }

  return (
    <button type="button" onClick={signIn} disabled={busy}
              data-testid="login-passkey-signin-button"
              className="w-full flex items-center justify-center gap-2 bg-white border-2 border-[#121614] hover:bg-[#F7F3E8] text-[#121614] rounded-full h-12 px-4 font-medium transition-colors">
      <Fingerprint size={18}/>
      <ScanFace size={18}/>
      <span>{busy ? t("login_passkey_waiting") : t("login_passkey_button")}</span>
    </button>
  );
}

export function PasskeyRegisterButton({ onRegistered }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [platformOk, setPlatformOk] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.PublicKeyCredential) return;
    window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable?.()
      .then((v) => setPlatformOk(!!v))
      .catch(() => setPlatformOk(false));
  }, []);

  async function register() {
    setBusy(true);
    try {
      const { data: opts } = await axios.post(
        `${API}/api/auth/passkey/register-challenge`, {},
        { withCredentials: true });
      const cred = await navigator.credentials.create({
        publicKey: pkOptsFromServer(opts),
      });
      if (!cred) throw new Error("no credential returned");
      await axios.post(
        `${API}/api/auth/passkey/verify-registration`,
        { credential: serializeAttestation(cred) },
        { withCredentials: true });
      toast.success(t("login_passkey_saved"));
      if (onRegistered) onRegistered();
    } catch (err) {
      if (err?.name === "NotAllowedError") {
        toast.error(t("login_passkey_registration_cancelled"));
      } else {
        toast.error(err?.response?.data?.detail
                      || err?.message
                      || t("login_passkey_register_failed"));
      }
    }
    setBusy(false);
  }

  if (typeof window !== "undefined"
        && !window.PublicKeyCredential) return null;
  return (
    <button type="button" onClick={register} disabled={busy}
              data-testid="passkey-register-button"
              className="w-full flex items-center justify-center gap-2 bg-[#F7F3E8] hover:bg-[#EEE7D3] text-[#121614] rounded-full h-11 px-4 text-sm font-medium">
      <KeyRound size={16}/>
      {busy ? t("login_passkey_waiting")
             : platformOk ? t("login_passkey_enable_biometric")
                             : t("login_passkey_add_security_key")}
    </button>
  );
}
